import React from 'react';
import { Page, Text, View, Document, StyleSheet, Font } from '@react-pdf/renderer';
import { v4 as uuidv4 } from 'uuid';

// Register Arial font
Font.register({
    family: 'Arial',
    src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf',
});

// Styles
const styles = StyleSheet.create({
    page: {
        fontSize: 12, // Reduced font size for better fit
        lineHeight: 1.5,
    },
    firstPage: {
        paddingTop: '2cm', // Reduced top padding
        paddingLeft: '2cm',
        paddingRight: '2cm',
        paddingBottom: '2cm',
        textAlign: 'justify',
    },
    otherPages: {
        paddingTop: '2cm',
        paddingLeft: '2cm',
        paddingRight: '2cm',
        paddingBottom: '2cm',
        textAlign: 'justify',
    },
    header: {
        textAlign: 'center',
        marginBottom: 10,
        fontSize: 14,
        fontWeight: 'bold',
        paddingBottom: '1cm',
    },
    paragraph: {
        marginBottom: 8,
    },
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 20,
        right: 20,
        fontSize: 8,
        textAlign: 'left',
        color: '#888888',
    },
    signature: {
        marginTop: 50,
        fontSize: 10,
        textAlign: 'right',
        fontWeight: 'bold',
    },
    indentedText: {
        paddingLeft: 15,
    },
});

// Function to generate security code (unchanged)
const generateSecurityCode = () => {
    const now = new Date();
    const dateString = now.toISOString().split('T')[0].replace(/-/g, '');
    const timeString = now.toTimeString().split(' ')[0].replace(/:/g, '');
    const uniqueId = uuidv4().split('-')[0];
    return `${dateString}${timeString}-${uniqueId}`;
};

// Create PDF document component
function PDF({ layout, formData, onSecurityCodeGenerated }) {
    const securityCode = generateSecurityCode();

    React.useEffect(() => {
        if (onSecurityCodeGenerated) {
            onSecurityCodeGenerated(securityCode);
        }
    }, [securityCode, onSecurityCodeGenerated]);

    let layoutContent;
    try {
        layoutContent = typeof layout.layoutContent === 'string'
            ? JSON.parse(layout.layoutContent)
            : layout.layoutContent;
    } catch (error) {
        console.error('Error parsing layoutContent:', error);
        return null;
    }

    // Function to replace tags with form data (unchanged)
    const replaceTagsWithFormData = (text) => {
        return text
            .replace(/@nombre_defendido/g, formData?.nombre || 'N/A')
            .replace(/@rut_defendido/g, formData?.rut || 'N/A')
            .replace(/@empresa/g, formData?.nombre || 'N/A');
           
    };

    // Improved function to split content into pages
    const splitContentIntoPages = (content) => {
        const pages = [];
        let currentPage = [];
        let lineCount = 0;
        const linesPerPage = 38; // Increased to allow more content per page

        content.forEach((item) => {
            const estimatedLines = Math.ceil(item.texto.length / 80) + (item.bienes ? item.bienes.length : 0) + 1; // +1 for item number
            if (lineCount + estimatedLines > linesPerPage && currentPage.length > 0) {
                pages.push(currentPage);
                currentPage = [];
                lineCount = 0;
            }
            currentPage.push(item);
            lineCount += estimatedLines;
        });
        if (currentPage.length > 0) {
            pages.push(currentPage);
        }
        return pages;
    };

    const contentPages = splitContentIntoPages(layoutContent.content);

    return (
        <Document>
            {contentPages.map((pageContent, pageIndex) => (
                <Page 
                    key={pageIndex} 
                    size="A4" 
                    style={[styles.page, pageIndex === 0 ? styles.firstPage : styles.otherPages]}
                >
                    {pageIndex === 0 && (
                        <>
                            <Text style={styles.header}>
                                {layoutContent?.tipo || 'Consentimiento Informado'}
                            </Text>
                            {layoutContent?.declaracion && (
                                <Text style={styles.paragraph}>
                                    {replaceTagsWithFormData(layoutContent.declaracion)}
                                </Text>
                            )}
                        </>
                    )}

                    {pageContent.map((item, index) => (
                        <View key={index} style={styles.paragraph}>
                            <Text>
                                <Text style={{ fontWeight: 'bold' }}>{`${item.numero}. `}</Text>
                                {replaceTagsWithFormData(item.texto)}
                            </Text>
                            {item.bienes && item.bienes.length > 0 && (
                                <View style={{ marginTop: 5 }}>
                                    {item.bienes.map((bien, bienIndex) => (
                                        <Text key={bienIndex} style={styles.indentedText}>
                                            • {replaceTagsWithFormData(bien)}
                                        </Text>
                                    ))}
                                </View>
                            )}
                        </View>
                    ))}

                    {pageIndex === contentPages.length - 1 && (
                        <View style={styles.signature}>
                            <Text>Firma del cliente: _________________________</Text>
                            <Text>NOMBRE: {formData?.nombre || 'N/A'}</Text>
                            <Text>C.I.: {formData?.rut || 'N/A'}</Text>
                        </View>
                    )}

                    <Text style={styles.footer}>
                        Código de seguridad: {securityCode} | Página {pageIndex + 1}/{contentPages.length} | Grupo Defensa 
                    </Text>
                </Page>
            ))}
        </Document>
    );
}

export default PDF;